import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Users, ArrowLeft, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openModal } = useOutletContext();
  const [space, setSpace] = useState(null);
  const [location, setLocation] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const spaceRes = await axios.get(`${API}/spaces/${id}`);
        setSpace(spaceRes.data);
        setCurrentImage(0);

        // location_id is now text, not an ID - use it directly
        setLocation({ name: spaceRes.data.location_id || 'Location not specified' });

        // Fetch all amenities to map IDs to names
        if (spaceRes.data.amenities && spaceRes.data.amenities.length > 0) {
          try {
            const amenitiesRes = await axios.get(`${API}/amenities`);
            const allAmenities = amenitiesRes.data;
            
            // Map amenity IDs to their names
            const resolvedAmenities = spaceRes.data.amenities
              .map(amenityId => {
                // Check if it's an ID (UUID format) or already a name
                const amenityObj = allAmenities.find(a => a.id === amenityId);
                if (amenityObj) {
                  return { name: amenityObj.name };
                }
                // If it's already a name (not an ID), use it directly
                return { name: amenityId };
              })
              .filter(Boolean);
            
            setAmenities(resolvedAmenities);
          } catch (amenityError) {
            console.error('Error fetching amenities:', amenityError);
            // Fallback: treat amenity values as names
            setAmenities(spaceRes.data.amenities.map(name => ({ name })));
          }
        }
      } catch (error) {
        console.error('Error fetching space details:', error);
      }
    };
    fetchData();
  }, [id]);

  if (!space) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const handleEnquire = () => {
    openModal({
      form_type: 'space_enquiry',
      space_name: space.name,
      workspace_type: space.type,
    });
  };

  return (
    <div className="pt-20" data-testid="space-detail-page">
      {/* Back Button */}
      <div className="bg-white py-4 border-b">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-[#666] hover:text-[#E12429] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div>
              <div className="relative h-96 rounded-lg overflow-hidden mb-4">
                <img
                  src={space.images[currentImage]}
                  alt={space.name}
                  className="w-full h-full object-cover"
                />
                {space.show_virtual_tour && space.virtual_tour_link && (
                  <button
                    onClick={() => window.open(space.virtual_tour_link, '_blank')}
                    className="absolute bottom-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-sm font-semibold hover:bg-white transition-all"
                  >
                    360° Virtual Tour
                  </button>
                )}
              </div>
              {space.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {space.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`h-20 rounded-lg overflow-hidden border-2 ${
                        currentImage === index ? 'border-[#E12429]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`${space.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-4xl font-bold text-[#222] mb-2">{space.name}</h1>
                  <p className="text-lg text-[#E12429] font-semibold mb-2">{space.type}</p>
                  <div className="flex items-center space-x-2 text-[#666]">
                    <MapPin size={16} />
                    <span>{location.name}</span>
                  </div>
                </div>
                {space.badges.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {space.badges.map((badge, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#E12429] text-white text-xs font-semibold rounded-full">
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-[#F5F5F5] p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2 text-[#222]">
                    <Users size={20} />
                    <span className="font-semibold">Capacity: {space.capacity}</span>
                  </div>
                  {space.price && (
                    <span className="text-2xl font-bold text-[#E12429]">{space.price}</span>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#222] mb-3">About This Space</h2>
                <p className="text-[#666] leading-relaxed">{space.description}</p>
              </div>

              <button
                onClick={handleEnquire}
                className="btn-primary w-full text-lg"
                data-testid="enquire-now-button"
              >
                Enquire Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="section-padding bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#222] mb-8">Amenities & Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenities.length > 0 ? (
              amenities.map((amenity, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#E12429] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#222] mb-1">{amenity.name}</h3>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-[#666]">No amenities listed for this space.</p>
            )}
          </div>
        </div>
      </section>

      {/* Location Map */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#222] mb-8">Location</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-[#222] mb-3">{location.name}</h3>
              <p className="text-[#666] mb-4">{space.map_address || space.location_id}</p>
              {space.show_directions !== false && space.google_maps_link && (
                <a
                  href={space.google_maps_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-block"
                  data-testid="get-directions-button"
                >
                  Get Directions
                </a>
              )}
            </div>
            <div className="h-80 rounded-lg overflow-hidden">
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent(space.map_address || space.location_id)}&output=embed`}
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title={location.name}
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}